import { ErrorMessage } from '@/components/loading-error';
import { MicrophonePermissionError } from '@/components/MicrophonePermissionError';
import { useVoiceEngine } from '@/hooks/useVoiceEngine';
import {
  getCustomLanguageFromURL,
  getLanguageConfig,
} from '@/utils/customLanguageUtils';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Menu,
  X,
  MessageSquare,
  ShoppingBag,
  AlertCircle,
  StopCircle,
} from 'lucide-react';
import dbsLogo from '@/assets/logos/dbsLogo.png';

// Components
import { LoadingOverlay } from '@/components/ui/loading';

// Store & Hooks
import { useCoachStore } from '@/store/coach';
import api from '@/utils/ky';
import { AudioControls } from './components/AudioControls';
import { Competitors } from './components/Competitors';
import { ConversationArea } from './components/ConversationArea';
import { FeedbackAnalysis } from './components/FeedbackAnalysis';
import { FrameworkObjectionGuide } from './components/FrameworkObjectionGuide';
import { ProductInfo } from './components/ProductInfo';
import { TranscriptReview } from './components/TranscriptReview';
import {
  type TtsConfig,
  DEFAULT_TTS_CONFIG,
} from './components/TtsConfigPanel';

// Define reverse role scenarios that don't need feedback
const REVERSE_ROLE_SCENARIOS = [
  'prudential-malaysia-3',
  'prudential-malaysia-4',
  'prudential-prusignature-plus-reverse-en',
  'prudential-prusignature-plus-reverse-ms',
  'aia-2',
];

export function CustomRolePlay() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scenarioCode = searchParams.get('scenario');
  const engine = useMemo(() => {
    const engineParam = searchParams.get('engine') as
      | 'elevenlabs'
      | 'openai'
      | 'livekit'
      | null;

    // If engine is explicitly specified in URL, use it
    if (engineParam && ['elevenlabs', 'openai', 'livekit'].includes(engineParam)) {
      return engineParam;
    }

    // Default to ElevenLabs for BNP scenarios
    if (scenarioCode?.startsWith('bnp-paribas')) {
      return 'elevenlabs';
    }

    return null;
  }, [searchParams, scenarioCode]) as 'elevenlabs' | 'openai' | 'livekit' | null;

  // Optional LiveKit language config preset (forwarded to /livekit/token).
  // E.g. ?config=yue-aisales for the MiniMax+DeepSeek Cantonese pipeline.
  const livekitConfigPreset = useMemo(
    () => searchParams.get('config') || undefined,
    [searchParams],
  );

  // Parse provider and voice from URL params (for voice override)
  const provider = useMemo(
    () => searchParams.get('provider') || undefined,
    [searchParams],
  );
  const voice = useMemo(
    () => searchParams.get('voice') || undefined,
    [searchParams],
  );

  const coachStore = useCoachStore();
  const { t, i18n } = useTranslation();

  // Language detection and setup
  const currentLanguage = getCustomLanguageFromURL();
  const languageConfig = getLanguageConfig(currentLanguage);

  console.log('🌐 Language Detection:', {
    currentLanguage,
    languageConfig,
    url: window.location.href,
    searchParams: Object.fromEntries(searchParams.entries()),
    timestamp: new Date().toISOString(),
  });

  // Set i18n language when component mounts
  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  // State
  const [loadingState, setLoadingState] = useState<
    | 'initial'
    | 'loading'
    | 'ready'
    | 'active'
    | 'ending'
    | 'error'
    | 'connecting'
  >('initial');
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [scenario, setScenario] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isPracticeStarted, setIsPracticeStarted] = useState(false);
  const [hasCompetitorData, setHasCompetitorData] = useState(false);
  const [competitorMentioned, setCompetitorMentioned] = useState(false);
  const [mobileView, setMobileView] = useState<'chat' | 'sales' | 'guide'>(
    'chat',
  );
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showMiniEndButton, setShowMiniEndButton] = useState(false);
  const [ttsConfig, setTtsConfig] = useState<TtsConfig>(() => {
    try {
      const saved = localStorage.getItem('ttsConfig');
      if (saved) return { ...DEFAULT_TTS_CONFIG, ...JSON.parse(saved) };
    } catch { /* ignore */ }
    return DEFAULT_TTS_CONFIG;
  });

  const handleTtsConfigChange = useCallback((config: TtsConfig) => {
    setTtsConfig(config);
    try {
      localStorage.setItem('ttsConfig', JSON.stringify(config));
    } catch { /* ignore */ }
  }, []);

  // Feedback stuff
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);

  const voiceEngine = useVoiceEngine(currentLanguage, engine);

  // Store a reference to the current voiceEngine
  const voiceEngineRef = useRef(voiceEngine);

  // Update the ref when voiceEngine changes
  useEffect(() => {
    voiceEngineRef.current = voiceEngine;
  }, [voiceEngine]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      const currentEngine = voiceEngineRef.current;
      if (
        currentEngine &&
        (currentEngine.status === 'connected' ||
          currentEngine.status === 'connecting')
      ) {
        console.log('🧹 Cleaning up voice engine on unmount');
        currentEngine.stop().catch((err) => {
          console.error('Error stopping voice engine during cleanup:', err);
        });
      }
    };
  }, []); // Empty dependency array ensures this only runs on unmount
  const { status, isSpeaking: isAISpeaking } = voiceEngine;

  // Refs
  const startTimeRef = useRef<number>(0);
  const [transcript, setTranscript] = useState<
    Array<{
      source: 'ai' | 'user';
      message: string;
      timestamp: Date;
    }>
  >([]);

  useEffect(() => {
    if (!scenarioCode) {
      navigate('/');
      return;
    }
    initializeScenario();
  }, [scenarioCode]);

  // Check if competitors are mentioned in the conversation
  useEffect(() => {
    // Simple detection logic - could be made more sophisticated
    const competitorTerms = [
      'competitor',
      'compare',
      'other product',
      'alternative',
      'other option',
      'different company',
      'other company',
      'similar product',
      'versus',
      'vs',
    ];

    // Check only AI messages
    const clientMessages = transcript
      .filter((msg) => msg.source === 'ai')
      .map((msg) => msg.message.toLowerCase());

    const mentioned = clientMessages.some((message) =>
      competitorTerms.some((term) => message.includes(term)),
    );

    if (mentioned) {
      setCompetitorMentioned(true);
    }
  }, [transcript]);

  const initializeScenario = async () => {
    try {
      setLoadingState('loading');

      // Fetch scenario details
      const scenarioData = await api
        .get(`api/custom/scenarios/${scenarioCode}?language=${currentLanguage}`)
        .json<any>();

      console.log('Scenario Data:', scenarioData);

      // Check if competitor data exists
      const hasCompetitors = !!(
        scenarioData?.scenario?.competitors &&
        Array.isArray(scenarioData.scenario.competitors) &&
        scenarioData.scenario.competitors.length > 0 &&
        scenarioData.scenario.prudentialAdvantages &&
        Array.isArray(scenarioData.scenario.prudentialAdvantages) &&
        scenarioData.scenario.prudentialAdvantages.length > 0
      );

      setHasCompetitorData(hasCompetitors);
      setScenario(scenarioData);
      console.log('Logo URL:', scenarioData?.scenario?.logo);
      setLoadingState('ready');
    } catch (error) {
      console.error('Error initializing scenario:', error);
      setError(t('custom.roleplay.errors.failedToLoadScenario'));
      setLoadingState('error');
    }
  };

  const handleStartPractice = async () => {
    // Prevent double-clicking
    if (loadingState === 'connecting') {
      console.log('⚠️ Already connecting, ignoring duplicate click');
      return;
    }

    try {
      setLoadingState('connecting');
      console.log('🚀 Starting practice session with config:', {
        userId: coachStore.userId,
        scenarioCode,
        currentLanguage,
        languageConfig,
        engine,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });

      // Create session
      const sessionData = await api
        .post('api/custom/sessions', {
          json: {
            userId: coachStore.userId,
            scenarioCode,
            language: currentLanguage,
          },
        })
        .json<{ sessionId: string }>();

      console.log('📝 Session created:', {
        sessionId: sessionData.sessionId,
        timestamp: new Date().toISOString(),
      });

      setSessionId(sessionData.sessionId);

      // Request microphone access
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('🎤 Microphone access granted');
      } catch (micError) {
        console.error('🎤 Microphone access error:', micError);

        // Check if we're in an iframe and if it's a permissions policy error
        const isIframe = window.self !== window.top;
        const isPermissionError =
          micError.name === 'NotAllowedError' ||
          micError.message?.includes('Permissions policy');

        if (isIframe && isPermissionError) {
          throw new Error('MICROPHONE_IFRAME_PERMISSION');
        } else if (micError.name === 'NotAllowedError') {
          throw new Error('MICROPHONE_DENIED');
        } else {
          throw micError;
        }
      }

      // Build query parameters for voice override
      const queryParams = new URLSearchParams();
      if (provider) queryParams.append('provider', provider);
      if (voice) queryParams.append('voice', voice);
      const queryString = queryParams.toString();

      const session = await api
        .get(
          `api/custom/sessions/${sessionData.sessionId}${queryString ? `?${queryString}` : ''}`,
        )
        .json<any>();

      console.log('📡 Retrieved session config:', {
        sessionId: sessionData.sessionId,
        agentId: session.aiConfig?.agentId,
        language: session.aiConfig?.language,
        voiceId: session.aiConfig?.voiceId,
        fullSession: session,
        voiceInstructionsLength: session.voiceInstructions?.chat?.length || 0,
        firstMessage: scenario.persona.initialMessage,
        engine,
        timestamp: new Date().toISOString(),
      });

      // Log language mismatch warning
      if (currentLanguage !== session.aiConfig?.language) {
        console.warn('⚠️ Language mismatch detected:', {
          frontendLanguage: currentLanguage,
          backendLanguage: session.aiConfig?.language,
          note: 'Backend may have converted the language code',
        });
      }

      console.log('Session ID:', session);

      const voiceConfig = {
        agentId: session.aiConfig?.agentId,
        language: currentLanguage, // Use the original language from URL, not the backend's converted one
        voiceId: session.aiConfig?.voiceId,
        prompt:
          session.voiceInstructions?.prompt ??
          session.voiceInstructions?.chat ??
          '',
        firstMessage: scenario.persona.initialMessage,
        // Add LiveKit specific fields
        roomName: session.sessionId,
        participantName: `user-${coachStore.userId || 'anonymous'}`,
        livekitConfigPreset,
        // Pass TTS config for voice tuning (e.g. aia-2 scenario)
        // For aia-2, frontend slider values override backend defaults
        ttsConfig:
          scenarioCode === 'aia-2'
            ? { ...session.aiConfig?.ttsConfig, ...ttsConfig }
            : session.aiConfig?.ttsConfig,
      };

      console.log('🔌 Initiating voice engine connection:', {
        engine,
        language: currentLanguage,
        config: {
          ...voiceConfig,
          prompt: voiceConfig.prompt?.substring(0, 200) + '...',
        },
        timestamp: new Date().toISOString(),
      });

      const conversationId = await voiceEngine.start(voiceConfig, {
        onConnect: () => {
          console.log(
            `✅ Successfully connected to ${engine || 'elevenlabs'} conversation`,
          );
          console.log('🔊 Connection details:', {
            timestamp: new Date().toISOString(),
            sessionId: sessionData.sessionId,
            agentId: session.aiConfig?.agentId,
            frontendLanguage: currentLanguage,
            backendLanguage: session.aiConfig?.language,
            voiceId: session.aiConfig?.voiceId,
            engine: engine || 'elevenlabs',
          });
          startTimeRef.current = Date.now();
          setLoadingState('active');
          setIsPracticeStarted(true);
        },
        onError: (error) => {
          console.error(`❌ ${engine || 'elevenlabs'} connection error:`, {
            error,
            errorMessage: error?.message,
            errorStack: error?.stack,
            errorType: error?.name,
            errorDetails: error,
            sessionId: sessionData.sessionId,
            agentId: session.aiConfig?.agentId,
            frontendLanguage: currentLanguage,
            backendLanguage: session.aiConfig?.language,
            voiceId: session.aiConfig?.voiceId,
            engine: engine || 'elevenlabs',
            timestamp: new Date().toISOString(),
          });
          setError(error?.message || error || 'Connection Error');
        },
        onMessage: ({ source, message }) => {
          console.log('💬 Message received:', {
            source,
            message,
            engine,
            timestamp: new Date().toISOString(),
          });
          const actualEngine = voiceEngineRef.current?.engine || engine;
          if (actualEngine === 'elevenlabs') {
            // Strip ElevenLabs expressive tags, Scribe V2 audio event tags (including unclosed), and XML-style tags
            const cleanMessage = message
              .replace(/\[[^\]]{2,}\]?/g, '')
              .replace(/<\/?[a-zA-Z][^>]*>/g, '')
              .replace(/\s{2,}/g, ' ')
              .trim();

            if (!cleanMessage) return;

            setTranscript((prev) => {
              // ElevenLabs SDK can fire onMessage twice for the same agent response,
              // sometimes with a user transcript sandwiched in between.
              // The later one is contextually correct (it's the actual response to user input),
              // so remove the earlier duplicate and append the new one.
              for (let i = prev.length - 1; i >= 0; i--) {
                if (prev[i].source === source) {
                  if (prev[i].message === cleanMessage) {
                    const filtered = [...prev];
                    filtered.splice(i, 1);
                    return [
                      ...filtered,
                      { source, message: cleanMessage, timestamp: new Date() },
                    ];
                  }
                  break;
                }
              }

              return [
                ...prev,
                { source, message: cleanMessage, timestamp: new Date() },
              ];
            });
          } else {
            setTranscript((prev) => [
              ...prev,
              {
                source,
                message,
                timestamp: new Date(),
              },
            ]);
          }
        },
        onDebug: (data) => {
          console.log('🐛 Debug data:', {
            data,
            engine,
            timestamp: new Date().toISOString(),
          });
        },
        onTranscript: (transcript, isFinal) => {
          console.log(
            `📝 Transcript ${isFinal ? 'final' : 'partial'}:`,
            transcript,
          );
        },
      });
      console.log('🎯 Conversation started successfully:', {
        conversationId,
        engine,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('💥 Error starting practice:', {
        error,
        errorMessage: error?.message,
        errorStack: error?.stack,
        scenarioCode,
        currentLanguage,
        timestamp: new Date().toISOString(),
      });

      // Set specific error message based on error type
      if (error?.message === 'MICROPHONE_IFRAME_PERMISSION') {
        setError('microphone_iframe_permission');
      } else if (error?.message === 'MICROPHONE_DENIED') {
        setError('microphone_denied');
      } else {
        setError(t('custom.roleplay.errors.failedToStartSession'));
      }

      setLoadingState('error');
    }
  };

  const handleEndPractice = async () => {
    try {
      setLoadingState('ending');

      if (voiceEngine) {
        await voiceEngine.stop();
      }

      // Check if this is a reverse role scenario
      const isReverseRole = REVERSE_ROLE_SCENARIOS.includes(scenarioCode || '');

      if (isReverseRole) {
        // For reverse role scenarios, just show the transcript without feedback
        setShowFeedback(true);
        setLoadingState('ready');
      } else {
        // For regular scenarios, generate feedback
        setIsLoadingFeedback(true);

        const formattedTranscript = transcript
          .map(
            (msg) =>
              `${msg.source === 'ai' ? 'Client' : 'Salesperson'}: ${msg.message}`,
          )
          .join('\n');

        const feedback = await api
          .post(`api/custom/sessions/${sessionId}/feedback`, {
            json: {
              transcript: formattedTranscript,
              duration: (Date.now() - startTimeRef.current) / 1000,
              hasCompetitorData,
              competitorMentioned,
              language: currentLanguage,
            },
            timeout: 90000,
          })
          .json();

        setFeedbackData(feedback);
        setShowFeedback(true);
        setIsLoadingFeedback(false);
        setLoadingState('ready');
      }
    } catch (error) {
      console.error('Error ending practice:', error);
      toast.error(t('custom.roleplay.errors.failedToGenerateFeedback'));
      setIsLoadingFeedback(false);
      setLoadingState('ready');
    }
  };

  const handlePracticeAgain = () => {
    setShowFeedback(false);
    window.location.reload();
  };

  console.log('🔄 isPracticeStarted: ', isPracticeStarted);

  return (
    <div className="h-screen flex flex-col lg:flex-row bg-slate-50 overflow-hidden relative">
      {/* Mobile Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-30">
        <div className="flex items-center justify-around h-16">
          <button
            onClick={() => setMobileView('chat')}
            className={`flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors ${
              mobileView === 'chat'
                ? 'text-primary bg-primary/5'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <MessageSquare className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">
              {t('custom.roleplay.mobile.conversation')}
            </span>
          </button>
          <button
            onClick={() => setMobileView('sales')}
            className={`flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors ${
              mobileView === 'sales'
                ? 'text-primary bg-primary/5'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ShoppingBag className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">
              {t('custom.roleplay.mobile.salesInfo')}
            </span>
          </button>
          <button
            onClick={() => setMobileView('guide')}
            className={`flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors ${
              mobileView === 'guide'
                ? 'text-primary bg-primary/5'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <AlertCircle className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">
              {t('custom.roleplay.mobile.guide')}
            </span>
          </button>
        </div>
      </div>

      {/* Left Panel - Sales Context */}
      <div
        className={`${mobileView === 'sales' ? 'flex' : 'hidden'} lg:flex w-full lg:w-1/4 lg:min-w-[300px] lg:max-w-[400px] h-full flex-shrink-0 bg-white lg:border-r border-slate-200 overflow-y-auto shadow-sm flex-col`}
      >
        <div className="sticky top-0 bg-white z-10 pt-4 px-4 pb-2 border-b border-slate-100">
          <div className="flex items-center justify-between">
            {scenario?.scenario?.logo ? (
              <div className="flex items-center justify-center py-2">
                <img
                  src={scenario.scenario.logo}
                  alt="Company Logo"
                  className="h-8 object-contain"
                  onError={(e) => {
                    console.error(
                      'Logo failed to load:',
                      scenario.scenario.logo,
                    );
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <h2 className="text-base lg:text-lg font-semibold text-slate-800 flex items-center">
                {scenarioCode?.includes('dbs') && (
                  <img
                    src={dbsLogo}
                    alt="DBS Logo"
                    className="h-6 object-contain mr-2"
                  />
                )}
                <span className="w-2 h-6 bg-primary rounded-sm mr-2 inline-block"></span>
                {t('custom.roleplay.headers.salesSupport')}
              </h2>
            )}
            <button
              onClick={() => setMobileView('chat')}
              className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="divide-y divide-slate-100 pb-20 lg:pb-0">
          <ProductInfo
            product={scenario?.scenario?.product}
            className="p-4 lg:p-6"
          />

          {/* Only render Competitors component if data exists */}
          {hasCompetitorData && scenario?.scenario?.competitors && (
            <Competitors
              competitors={scenario.scenario.competitors}
              prudentialAdvantages={scenario.scenario.prudentialAdvantages}
              className="p-4 lg:p-6"
            />
          )}
        </div>
      </div>

      {/* Main Area - Conversation */}
      <div
        className={`${mobileView === 'chat' ? 'flex' : 'hidden'} lg:flex flex-1 h-full flex-col bg-gradient-to-b from-slate-50 to-white relative`}
      >
        {showFeedback ? (
          <div className="flex-1 overflow-y-auto px-4 py-6 pb-20 lg:pb-6">
            {REVERSE_ROLE_SCENARIOS.includes(scenarioCode || '') ? (
              <TranscriptReview
                transcript={transcript}
                onPracticeAgain={handlePracticeAgain}
              />
            ) : (
              <FeedbackAnalysis
                feedback={feedbackData}
                framework={scenario?.scenario?.framework}
                onPracticeAgain={handlePracticeAgain}
                sessionId={sessionId}
                hasCompetitorData={hasCompetitorData && competitorMentioned}
                scenarioCode={scenarioCode || undefined}
              />
            )}
          </div>
        ) : (
          <>
            {/* Mobile Floating End Session Button - Fixed at bottom right */}
            {isPracticeStarted && (
              <motion.div
                className="lg:hidden fixed bottom-20 right-4 z-40"
                initial={{ scale: 0, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0, opacity: 0, y: 20 }}
                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleEndPractice}
                  className="bg-red-600 text-white rounded-full shadow-2xl hover:bg-red-700 transition-all flex items-center justify-center relative group px-5 py-3"
                  style={{
                    boxShadow: '0 8px 24px rgba(220, 38, 38, 0.4)',
                  }}
                >
                  <StopCircle className="w-5 h-5 mr-2" />
                  <span className="font-medium text-sm">
                    {t('custom.roleplay.buttons.endSession')}
                  </span>
                </motion.button>
              </motion.div>
            )}
            <ConversationArea
              transcript={transcript}
              isAISpeaking={isAISpeaking}
              persona={scenario?.persona}
              isPracticeStarted={isPracticeStarted}
              onStartPractice={handleStartPractice}
              sessionId={sessionId}
              hasCompetitorData={hasCompetitorData}
              engine={engine}
              isConnecting={loadingState === 'connecting'}
              scenarioCode={scenarioCode}
              ttsConfig={ttsConfig}
              onTtsConfigChange={handleTtsConfigChange}
              className="flex-1 overflow-y-auto p-4 lg:p-6 pb-24 lg:pb-6"
            />
            {/* Desktop End Session Button - At Bottom */}
            {isPracticeStarted && (
              <AudioControls
                isRecording={isRecording}
                setIsRecording={setIsRecording}
                onEndSession={handleEndPractice}
                className="hidden lg:block p-4 lg:p-5 border-t border-slate-200 bg-white shadow-md-up"
              />
            )}
          </>
        )}
      </div>

      {/* Right Panel - Real-time Feedback */}
      <div
        className={`${mobileView === 'guide' ? 'flex' : 'hidden'} lg:flex w-full lg:w-1/4 lg:min-w-[300px] lg:max-w-[400px] h-full flex-shrink-0 bg-white lg:border-l border-slate-200 overflow-y-auto shadow-sm flex-col`}
      >
        <div className="sticky top-0 bg-white z-10 pt-4 px-4 pb-2 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h2 className="text-base lg:text-lg font-semibold text-slate-800 flex items-center">
              <span className="w-2 h-6 bg-amber-500 rounded-sm mr-2 inline-block"></span>
              {t('custom.roleplay.headers.handlingGuide')}
            </h2>
            <button
              onClick={() => setMobileView('chat')}
              className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="pb-20 lg:pb-0">
          <FrameworkObjectionGuide
            framework={scenario?.scenario?.framework}
            objections={scenario?.persona?.objections}
            scenarioCode={scenarioCode}
            className="p-4 lg:p-6"
          />
        </div>
      </div>

      {/* Loading States */}
      <AnimatePresence>
        {loadingState !== 'ready' && loadingState !== 'active' && (
          <LoadingOverlay state={loadingState} />
        )}
      </AnimatePresence>

      {/* Error Message */}
      {error &&
        (error === 'microphone_iframe_permission' ||
        error === 'microphone_denied' ? (
          <MicrophonePermissionError
            errorType={error}
            onRetry={initializeScenario}
            onClose={() => setError(null)}
          />
        ) : (
          <ErrorMessage message={error} onRetry={initializeScenario} />
        ))}
    </div>
  );
}
